// Карточка сделки — самая главная и самая заполняемая.
//
// ЗАЧЕМ ОТДЕЛЬНЫМ ФАЙЛОМ. Сделка — единственная сущность, которую заводят каждый день и
// заводят руками. Владелец просил отдельно: заполнять её должно быть легко. «Легко» здесь
// не про красоту, а про порядок: сначала то, без чего сделки нет вовсе (название, заказчик,
// стадия, ответственный, сумма), потом деньги и налог, потом сроки и судно, потом условия,
// и только в конце — роли, документы и разбор проигрыша. Поле, стоящее не там, стоит
// человеку захода в карточку второй раз.
//
// ЧТО СЛОМАЕТСЯ БЕЗ ЭТОГО ФАЙЛА. Карточку сделки нечем нарисовать: общий код умеет рисовать
// любую карточку, но не знает ни одного поля. Порядок групп в массиве `группы` — это и есть
// порядок на экране, другого места, где он задан, нет.
//
// ОТКУДА ПОЛЯ. `cir-core/МОДЕЛЬ_ДАННЫХ.md` §5.2, таблица `deal`, — состав и обязательность
// колонка в колонку. Русские подписи и их порядок по заполненности — §2.4. Стадии — §2.3.
// Деньги (сумма, валюта, курс, дата курса, рублёвый эквивалент) — §5.3.
//
// ЧЕГО ЗДЕСЬ НЕТ НАРОЧНО. Полей-кнопок бизнес-процессов («КП одобрено», «Обновить»): ключ
// у них стоит на каждой сделке, а значение всегда `'0'` — это действие, а не поле (§5.0,
// решение седьмое, разбор в §6.2). Их место — в `действия`, и то только у тех, что мы
// действительно умеем делать сами.

import { fmtDate, fmtMoney, todayIso } from '../../lib/format'
import { деньги, пусто, строка, ярлык } from '../values'
import type { ОписаниеКарточки } from '../types'

import {
  ВОРОНКИ,
  илиПрочерк,
  подписьСтадии,
  ПОДРАЗДЕЛЕНИЯ,
  проиграна,
  СТАВКИ_НДС,
  СТАДИЯ_УСПЕХА,
  СТАДИИ,
  СТАДИИ_ВОРОНКИ,
  ТИПЫ_СДЕЛКИ,
} from './obshchee'

export const СДЕЛКА: ОписаниеКарточки = {
  сущность: 'сделка',
  один: 'сделка',
  винительный: 'сделку',
  много: 'Сделки',
  документ: true,
  путь: (id) => `/cards/deal/${id}`,
  заголовок: (з) => илиПрочерк(строка(з, 'title')),

  показатели: [
    {
      код: 'сумма',
      подпись: 'Сумма',
      значение: (з) => {
        const д = деньги(з, 'amount')
        return д.сумма === null ? '—' : fmtMoney(д.сумма, д.валюта)
      },
      тревога: (з) => пусто(з.amount),
    },
    {
      код: 'стадия',
      подпись: 'Стадия',
      значение: (з) => подписьСтадии(строка(з, 'stage_id'), строка(з, 'pipeline_id')),
      тревога: проиграна,
    },
    {
      код: 'срок',
      подпись: 'Завершение',
      значение: (з) => (пусто(з.closed_at) ? '—' : fmtDate(строка(з, 'closed_at'))),
      // Тревога — не «дата в прошлом», а «дата в прошлом, а сделка ещё в работе».
      // Закрытая позавчера сделка нормальна; открытая с позавчерашним сроком — нет.
      тревога: (з) => {
        const срок = строка(з, 'closed_at')
        if (срок === '') return false
        const финал = строка(з, 'stage_id') === СТАДИЯ_УСПЕХА || проиграна(з)
        return !финал && срок < todayIso()
      },
    },
    {
      код: 'судно',
      подпись: 'Судно',
      значение: (з) => ярлык(з, 'vessel_id')?.название ?? '—',
    },
  ],

  группы: [
    {
      код: 'кто',
      заголовок: 'Кто и о чём',
      вкладка: 'основное',
      подсказка: 'Пять полей, без которых сделки не существует. Остальное можно дописать потом.',
    },
    {
      код: 'деньги',
      заголовок: 'Деньги',
      вкладка: 'основное',
      подсказка: 'Сумма и валюта врозь, курс — на дату документа. Рубли считает база.',
    },
    { код: 'сроки', заголовок: 'Сроки и судно', вкладка: 'основное' },
    {
      код: 'условия',
      заголовок: 'Условия',
      вкладка: 'основное',
      подсказка: 'Текст в счёт и в договор: пишется один раз и переносится в документы как есть.',
    },
    {
      код: 'роли',
      заголовок: 'Кто ещё в работе',
      вкладка: 'связи',
      подсказка: 'Роли на сделке. Их больше, чем колонок: редкие живут отдельной таблицей исполнителей.',
    },
    { код: 'бумаги', заголовок: 'Договор и номер', вкладка: 'связи' },
    {
      код: 'итог',
      заголовок: 'Чем кончилось',
      вкладка: 'основное',
      подсказка: 'Причина проигрыша — единственное, ради чего эту группу открывают.',
    },
    { код: 'вложения', заголовок: 'Вложения', вкладка: 'документы' },
  ],

  поля: [
    // ─── Кто и о чём ───
    {
      код: 'title',
      подпись: 'Название',
      вид: 'строка',
      группа: 'кто',
      обязательно: true,
      почемуОбязательно: 'по названию сделку ищут в списке — «Без названия» там будет десяток',
      подсказкаВвода: 'Судно, работа, месяц — «Радиодевиация т/х Балтика, март»',
      максДлина: 200,
    },
    {
      код: 'company_id',
      подпись: 'Заказчик',
      вид: 'связь',
      связьС: 'компания',
      группа: 'кто',
      обязательно: true,
      почемуОбязательно: 'без контрагента нельзя выставить ни счёт, ни акт',
      подсказка: 'Подставляется от судна — судовладелец. Если платит другой, поменяйте руками.',
      подстановка: { из: 'vessel_id', взять: 'owner_company_id', толькоВПустое: true },
    },
    {
      код: 'person_id',
      подпись: 'Контактное лицо',
      вид: 'связь',
      связьС: 'контакт',
      группа: 'кто',
      подсказка: 'Человек, а не судно: судно теперь отдельная карточка и живёт в поле ниже.',
    },
    {
      код: 'pipeline_id',
      подпись: 'Воронка',
      вид: 'список',
      варианты: ВОРОНКИ,
      группа: 'кто',
      обязательно: true,
      почемуОбязательно: 'стадии у каждой воронки свои, и без воронки стадия ничего не значит',
      подсказка: 'Выбирается первой: список стадий ниже собирается под неё.',
    },
    {
      код: 'stage_id',
      подпись: 'Стадия',
      вид: 'список',
      варианты: СТАДИИ,
      вариантыПо: { поле: 'pipeline_id', наборы: СТАДИИ_ВОРОНКИ },
      группа: 'кто',
      обязательно: true,
      почемуОбязательно: 'по стадии считается воронка продаж — сделка вне стадии в неё не попадёт',
      подсказка: 'Только стадии выбранной воронки: один и тот же код в разных воронках — разные стадии с разными подписями.',
    },
    {
      код: 'deal_type_id',
      подпись: 'Тип работ',
      вид: 'список',
      варианты: ТИПЫ_СДЕЛКИ,
      группа: 'кто',
      подсказка: 'Чем отличается эта сделка от соседней: по типу собирается отчёт по направлениям.',
    },
    {
      код: 'assigned_id',
      подпись: 'Ответственный',
      вид: 'связь',
      связьС: 'сотрудник',
      группа: 'кто',
      обязательно: true,
      почемуОбязательно: 'сделка без ответственного не попадёт ни в чей список задач',
      подсказка: 'Подставляется из карточки заказчика: кто ведёт клиента, тот обычно ведёт и сделку.',
      подстановка: { из: 'company_id', взять: 'assigned_id', толькоВПустое: true },
    },

    // ─── Деньги ───
    {
      код: 'amount',
      подпись: 'Сумма',
      вид: 'деньги',
      группа: 'деньги',
      обязательно: true,
      почемуОбязательно: 'сумма — единственное, по чему сделку сравнивают с другими',
      подсказка: 'Ставится сама из состава сделки; галочка рядом — если сумму назвали руками.',
    },
    {
      код: 'amount_is_manual',
      подпись: 'Сумма поставлена руками',
      вид: 'флаг',
      группа: 'деньги',
      подсказка: 'Состава нет почти у половины сделок, поэтому сумму часто вписывают. Галочка говорит отчёту, что пересчитывать по составу её не надо.',
    },
    {
      код: 'fx_rate',
      подпись: 'Курс к рублю',
      вид: 'число',
      число: true,
      группа: 'деньги',
      обязательно: true,
      почемуОбязательно: 'без курса рублёвый эквивалент не посчитать, а по нему идёт вся отчётность',
      подсказка: 'Курс к рублю на дату документа, а не сегодняшний: иначе прошлогодний отчёт меняется каждый день.',
    },
    {
      код: 'fx_date',
      подпись: 'Дата курса',
      вид: 'дата',
      группа: 'деньги',
      обязательно: true,
      почемуОбязательно: 'курс без даты нечем проверить',
      подсказка: 'День, на который взят курс. Обычно совпадает с датой открытия сделки.',
    },
    {
      код: 'amount_rub',
      подпись: 'Сумма в рублях',
      вид: 'деньги',
      группа: 'деньги',
      толькоЧтение: true,
      почемуНельзяПравить: 'считает база: сумма, умноженная на курс. Правьте сумму или курс — рубли пересчитаются сами',
    },
    {
      код: 'vat_rate_id',
      подпись: 'Ставка НДС',
      вид: 'список',
      варианты: СТАВКИ_НДС,
      группа: 'деньги',
      обязательно: true,
      почемуОбязательно: 'ставка попадает в счёт и в акт, а задним числом её не поменять',
      подсказка: 'Ставка на дату документа. Внутри одного года ставка уже менялась — старые документы остаются со своей.',
    },

    // ─── Сроки и судно ───
    {
      код: 'opened_at',
      подпись: 'Открыта',
      вид: 'дата',
      группа: 'сроки',
      обязательно: true,
      почемуОбязательно: 'от этой даты считается всё: срок, воронка, отсечка отчётного периода',
    },
    {
      код: 'closed_at',
      подпись: 'Плановое завершение',
      вид: 'дата',
      группа: 'сроки',
      подсказка: 'Когда обещали закончить. Дата в прошлом у незакрытой сделки подсвечивается в шапке.',
    },
    {
      код: 'vessel_id',
      подпись: 'Судно',
      вид: 'связь',
      связьС: 'судно',
      группа: 'сроки',
      подсказка: 'Отдельная карточка, а не контакт с фамилией «Судно». По ней собирается вся история работ на этом судне.',
    },
    {
      код: 'eta',
      подпись: 'ETA — приход',
      вид: 'дата',
      группа: 'сроки',
      подсказка: 'Когда судно встаёт к причалу. По ETA планируется выезд инженера, поэтому дата тухнет быстрее прочих.',
    },
    {
      код: 'etd',
      подпись: 'ETD — отход',
      вид: 'дата',
      группа: 'сроки',
      подсказка: 'Крайний срок работ: после отхода на борт уже не попасть.',
    },
    {
      код: 'work_start_at',
      подпись: 'Выход на судно',
      вид: 'дата',
      группа: 'сроки',
    },
    {
      код: 'work_end_at',
      подпись: 'Примерное завершение работ',
      вид: 'дата',
      группа: 'сроки',
    },
    {
      код: 'berth',
      подпись: 'Место стоянки',
      вид: 'строка',
      группа: 'сроки',
      подсказкаВвода: 'Порт, причал, рейд',
      подсказка: 'Куда ехать инженеру. Пишется как в заявке капитана, без сокращений своего сочинения.',
      максДлина: 200,
    },

    // ─── Условия ───
    {
      код: 'payment_terms',
      подпись: 'Условия оплаты',
      вид: 'текст',
      группа: 'условия',
      подсказка: 'Текст уезжает в счёт дословно. «Постоплата 30 дней» и «в течение месяца» — разные обязательства, пишите то, что подписали.',
    },
    {
      код: 'delivery_terms',
      подпись: 'Условия поставки',
      вид: 'текст',
      группа: 'условия',
      подсказка: 'Кто везёт, до какого порта и за чей счёт.',
    },
    {
      код: 'other_terms',
      подпись: 'Прочие условия',
      вид: 'текст',
      группа: 'условия',
    },

    // ─── Кто ещё в работе ───
    {
      код: 'manager_id',
      подпись: 'Менеджер отдела продаж',
      вид: 'связь',
      связьС: 'сотрудник',
      группа: 'роли',
    },
    {
      код: 'coordinator_id',
      подпись: 'Координатор',
      вид: 'связь',
      связьС: 'сотрудник',
      группа: 'роли',
      подсказка: 'Кто сводит инженера, судно и заказчика по времени.',
    },
    {
      код: 'senior_engineer_id',
      подпись: 'Старший инженер',
      вид: 'связь',
      связьС: 'сотрудник',
      группа: 'роли',
      подсказка: 'Он же подписывает акт, поэтому подставляется в акт при выборе сделки.',
    },
    {
      код: 'org_unit_id',
      подпись: 'Подразделение',
      вид: 'список',
      варианты: ПОДРАЗДЕЛЕНИЯ,
      группа: 'роли',
      обязательно: true,
      почемуОбязательно: 'по подразделению делится выручка между регионами',
      подсказка: 'Подставляется из карточки ответственного. Если работу ведёт другой регион — поменяйте.',
      подстановка: { из: 'assigned_id', взять: 'org_unit_id', толькоВПустое: true },
    },
    {
      код: 'own_company_id',
      подпись: 'Наша компания',
      вид: 'связь',
      связьС: 'компания',
      группа: 'роли',
      обязательно: true,
      почемуОбязательно: 'от кого выставляем документы — иначе счёт не собрать',
      подсказка: 'Подставляется из договора: юрлицо стороны менять по сделке нельзя.',
      подстановка: { из: 'contract_id', взять: 'own_company_id', толькоВПустое: true },
    },

    // ─── Договор и номер ───
    {
      код: 'contract_id',
      подпись: 'Договор',
      вид: 'связь',
      связьС: 'договор',
      группа: 'бумаги',
      подсказка: 'Настоящая ссылка на карточку договора, а не код договора текстом: по текстовому коду ни один отчёт собрать не удалось.',
    },
    {
      код: 'no',
      подпись: 'Номер сделки',
      вид: 'строка',
      группа: 'бумаги',
      толькоЧтение: true,
      почемуНельзяПравить: 'выдаёт нумератор при выпуске из черновика — руками номер уже был свободным текстом, и уникальным он быть перестал',
    },

    // ─── Чем кончилось ───
    {
      код: 'lost_reason',
      подпись: 'Почему проиграли',
      вид: 'текст',
      группа: 'итог',
      обязательно: проиграна,
      почемуОбязательно: 'проигрыш без причины не разобрать: через полгода никто не вспомнит, дело было в цене или в сроке',
      подсказка: 'Появляется обязательным при переводе сделки в отмену — любой из двух.',
    },

    // ─── Вложения ───
    {
      код: 'files',
      подпись: 'Файлы сделки',
      вид: 'файл',
      группа: 'вложения',
      подсказка: 'Своё хранилище, а не файлы Bitrix: те не переносятся. У каждого вложения есть роль — по ней документ находят, а не по имени файла.',
    },
  ],

  обратные: [
    {
      код: 'счета',
      заголовок: 'Счета по сделке',
      сущность: 'счёт',
      поле: 'deal_id',
      колонки: [
        { код: 'no', подпись: 'Номер' },
        { код: 'issued_at', подпись: 'Выставлен' },
        { код: 'amount', подпись: 'Сумма', число: true },
        { код: 'paid_at', подпись: 'Оплачен' },
      ],
    },
    {
      код: 'акты',
      заголовок: 'Акты по сделке',
      сущность: 'акт',
      поле: 'deal_id',
      колонки: [
        { код: 'no', подпись: 'Номер' },
        { код: 'act_date', подпись: 'Дата' },
        { код: 'hours_total', подпись: 'Часы', число: true },
        { код: 'amount', подпись: 'Сумма', число: true },
      ],
    },
  ],

  действия: [
    {
      код: 'кп',
      подпись: 'Собрать КП',
      главное: true,
      почемуНельзя: (з) => {
        if (пусто(з.company_id)) return 'Некому адресовать: не выбран заказчик'
        if (пусто(з.amount)) return 'Нечего предлагать: не названа сумма'
        return null
      },
    },
    {
      код: 'счёт',
      подпись: 'Выставить счёт',
      почемуНельзя: (з) => {
        if (пусто(з.own_company_id)) return 'Не выбрана наша компания — от кого выставлять'
        if (пусто(з.vat_rate_id)) return 'Не выбрана ставка НДС: счёт без ставки не примут'
        if (проиграна(з)) return 'Сделка отменена — счёт по ней не выставляют'
        return null
      },
    },
  ],
}
